import * as React from 'react'
import cx from 'classnames'
import Icon from 'components/Icon'
import { EMPTY, Observable } from 'rxjs'
import { mergeRefs } from 'utils/react-util'
import { useObservableFocus } from 'hooks/focus-hooks'
import styles from './SearchField.sass'

interface SearchFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  onChange(newValue: string): unknown

  dataTest?: string
  stretch?: boolean
  focus$?: Observable<unknown>
  showClearButton?: boolean
  embedded?: boolean
}

/**
 * Search field component, for use in headers.
 */
export default React.forwardRef(function SearchField(
  {
    value,
    className,
    onChange,
    stretch,
    focus$ = EMPTY,
    showClearButton = false,
    dataTest = 'search-field',
    embedded = false,
    ...rest
  }: SearchFieldProps,
  ref: React.Ref<HTMLInputElement>,
) {
  const isEmpty = !value
  const inputRef = React.useRef<HTMLInputElement>(null)
  useObservableFocus(focus$, inputRef)
  const clearSearch = React.useCallback(() => {
    onChange('')
  }, [onChange])

  const change = React.useCallback(
    (e: any) => {
      onChange(e.target.value)
    },
    [onChange],
  )

  return (
    <div
      className={cx(
        styles.root,
        stretch && styles.stretch,
        embedded && styles.backgroundTransparent,
        className,
      )}
      onClick={focus}
    >
      <span className={styles.searchIcon}>
        <Icon auto name="search" />
      </span>
      <input
        ref={mergeRefs([ref, inputRef])}
        autoComplete="off"
        type="text"
        placeholder="Search"
        className={cx(styles.input, isEmpty && styles.empty)}
        onChange={change}
        value={value}
        data-test={dataTest}
        {...rest}
      />

      {!showClearButton && (
        <a
          className={cx(styles.closeIcon, isEmpty && styles.invisible)}
          onClick={clearSearch}
        >
          <Icon auto name="close" />
        </a>
      )}

      {showClearButton && (
        <button
          className={cx(styles.clearTextButton, isEmpty && styles.invisible)}
          onClick={clearSearch}
        >
          Clear
        </button>
      )}
    </div>
  )
})
